// src/hooks/useScheduleTripNotifications.js
//
// ============================================================
// WHAT THIS FILE DOES
//
// Writes rows to the scheduled_notifications table whenever a
// trip is saved or updated. The existing Edge Function cron
// (send-gig-reminders) picks them up and fires via OneSignal --
// no new infrastructure needed.
//
// Mirrors the same warning + now pattern used for gig notifications:
//   warning fires advanceMin minutes BEFORE the event
//   now    fires exactly AT  the event
//
// NOTIFICATION TYPES WRITTEN (embedded trip ID lets clearForTrip
// delete all rows for one trip with a single LIKE query)
//
//   travel_leave_warning:TRIP_ID          round trip: warn before leaving home
//   travel_leave_now:TRIP_ID              round trip: leave home for terminal now
//   travel_return_warning:TRIP_ID         round trip: warn before leaving destination
//   travel_return_now:TRIP_ID             round trip: leave destination for terminal now
//   travel_pickup_warning:TRIP_ID         round trip: warn before pickup arrives
//   travel_pickup_now:TRIP_ID             round trip: pickup is here
//   travel_leave_warning:TRIP_ID:N        multi-leg: warn before leg N departure
//   travel_leave_now:TRIP_ID:N            multi-leg: leave for leg N terminal now
//   travel_pickup_warning:TRIP_ID:N       multi-leg: warn before leg N pickup
//   travel_pickup_now:TRIP_ID:N           multi-leg: leg N pickup is here
//
// WHY gig_id IS null FOR TRIP ROWS
//   scheduled_notifications.gig_id allows NULL (no NOT NULL constraint
//   and no DEFAULT). A null FK value does not violate the FK constraint
//   in PostgreSQL. Trip rows are identified by notif_type instead.
//
// CALLED FROM
//   calendar.js handleSaveTrip   -- after every addTrip / updateTrip
//   calendar.js handleDeleteTrip -- before deleteTrip, to clear rows
//
// PICKUP TIME ON TRIPS
//   Add an optional `pickup_time` ISO datetime field to the trip
//   object (and to AddTripModal) if you want pickup notifications.
//   Round trip: trip.pickup_time
//   Multi-leg:  leg.pickup_time  (per leg, e.g. taxi to outbound airport)
//   If the field is absent, pickup rows are simply not written.
// ============================================================

import { useCallback } from 'react'
import { supabase }    from '../lib/supabaseClient'

// Rows whose send_at is older than this many hours are dropped.
// Matches the same constant in useScheduleNotifications.js.
const STALE_HOURS = 24


// ============================================================
// parseISO
//
// Safely parses an ISO datetime string into a Date.
// Returns null if the input is missing or not a valid date.
// Trip departure datetimes are full ISO strings (with timezone)
// so new Date() parses them correctly without manual conversion.
// ============================================================
function parseISO(str) {
  if (!str) return null
  const d = new Date(str)
  // new Date() on an unparseable string returns an "Invalid Date"
  // object whose getTime() returns NaN
  return isNaN(d.getTime()) ? null : d
}


// ============================================================
// useScheduleTripNotifications HOOK
// ============================================================

export function useScheduleTripNotifications() {


  // -------------------------------------------------------
  // clearForTrip
  //
  // Deletes ALL scheduled_notifications rows that belong to
  // a given trip. Call this BEFORE inserting new rows when a
  // trip is edited, so stale and fresh rows never coexist.
  // Also call it when a trip is deleted.
  //
  // HOW THE LIKE PATTERN WORKS
  //   Every row written by this hook encodes the trip UUID
  //   immediately after the first ':' in notif_type, e.g.:
  //     travel_leave_warning:f47ac10b-58cc-4372-a567-0e02b2c3d479
  //     travel_leave_now:f47ac10b-58cc-4372-a567-0e02b2c3d479:2
  //   '%:TRIP_UUID%' matches every variant regardless of
  //   the prefix or optional leg-index suffix.
  //   UUIDs contain only hex digits and hyphens, so ':' is a
  //   safe delimiter that cannot collide with the UUID itself.
  // -------------------------------------------------------
  const clearForTrip = useCallback(async (tripId, userId) => {
    if (!tripId || !userId) return
    // cannot clear without knowing which trip and which user to target

    const { error } = await supabase
      .from('scheduled_notifications')
      .delete()
      .eq('user_id', userId)
      // scope to this user so we never touch another user's rows
      .like('notif_type', `%:${tripId}%`)
      // matches travel_leave_warning:TRIP_ID,
      //         travel_leave_now:TRIP_ID:0, etc.

    if (error) {
      console.error('[useScheduleTripNotifications] clearForTrip error:', error.message)
    } else {
      console.log('[useScheduleTripNotifications] Cleared all notification rows for trip', tripId)
    }
  }, [])


  // -------------------------------------------------------
  // scheduleForTrip
  //
  // Calculates notification send times for all departure and
  // pickup events on a trip, then writes them to
  // scheduled_notifications in a single batch insert.
  //
  // trip   = the full trip object as stored in useTripMode
  // userId = UUID of the logged-in performer
  //
  // CALL PATTERN (matches how gig scheduling is called):
  //   await scheduleForTrip(savedTrip, userId)
  //   -- always await so errors surface before the modal closes
  // -------------------------------------------------------
  const scheduleForTrip = useCallback(async (trip, userId) => {

    if (!trip?.id || !userId) {
      console.warn('[useScheduleTripNotifications] Missing trip.id or userId; skipping.')
      return
    }

    // Clear any existing rows for this trip FIRST.
    // If the user edited departure times, we must not leave
    // the old send_at rows in the DB alongside the new ones.
    await clearForTrip(trip.id, userId)

    // --------------------------------------------------------
    // Load the user's advance warning preference and check
    // that a subscription ID is on file so rows will actually
    // deliver when the Edge Function cron picks them up.
    // --------------------------------------------------------
    const { data: prefs } = await supabase
      .from('notification_preferences')
      .select('advance_notice_minutes, react_subscription_id')
      .eq('user_id', userId)
      .single()

    const advanceMin = prefs?.advance_notice_minutes || 30
    // default to 30 minutes if the user has not set a preference

    if (!prefs?.react_subscription_id) {
      // Rows will still be written. The Edge Function skips rows
      // whose user has no subscription ID, and will deliver them
      // once the user enables notifications in Settings.
      console.warn(
        '[useScheduleTripNotifications] No react_subscription_id for user', userId,
        '-- rows written but Edge Function will skip until notifications are enabled.'
      )
    }

    const now         = new Date()
    // the current moment in UTC; used to classify past vs future times

    const staleCutoff = new Date(now.getTime() - STALE_HOURS * 60 * 60 * 1000)
    // any send_at older than STALE_HOURS is silently dropped

    const advanceMs = advanceMin * 60 * 1000
    // pre-compute milliseconds so we do not repeat the multiplication below

    const rows = []
    // the batch of row objects we will insert at the end;
    // building the full list first means a single DB round-trip


    // --------------------------------------------------------
    // toSendAt
    //
    // Converts an ISO departure datetime + an optional offset
    // (in milliseconds) into a send_at Date for a row.
    //
    // Returns:
    //   future Date  -- schedule normally
    //   current Date -- time is in the recent past; fire ASAP
    //   null         -- time is too stale; drop this row
    //
    // offsetMs is negative for warning notifications because
    // they fire BEFORE the event (e.g. -30 * 60000 = 30 min early).
    // --------------------------------------------------------
    function toSendAt(isoStr, offsetMs) {
      const base = parseISO(isoStr)
      if (!base) return null
      // missing or invalid datetime -- nothing to schedule

      const adjusted = new Date(base.getTime() + (offsetMs || 0))
      // offsetMs defaults to 0 so a "now" row fires exactly at the event

      if (adjusted > now) return adjusted
      // time is in the future -- schedule it as-is

      if (adjusted <= staleCutoff) {
        // More than STALE_HOURS in the past -- skip entirely
        console.log(
          '[useScheduleTripNotifications] Dropping stale time:',
          adjusted.toISOString(),
          '(trip:', trip.id + ')'
        )
        return null
      }

      // Time passed recently (within STALE_HOURS).
      // Return the current moment so the cron fires within 1 minute.
      console.log(
        '[useScheduleTripNotifications] Recent past -> fire ASAP:',
        adjusted.toISOString(),
        '(was', Math.round((now - adjusted) / 60000), 'min ago)'
      )
      return new Date(now.getTime())
    }


    // --------------------------------------------------------
    // addRow
    //
    // Appends one notification row to the batch.
    // notifType  = the type string (with trip ID embedded)
    // sendAtDate = result from toSendAt(); skipped if null
    // --------------------------------------------------------
    function addRow(notifType, sendAtDate) {
      if (!sendAtDate) return
      // null means the time was stale or missing -- omit this row

      rows.push({
        user_id:    userId,
        // who this notification belongs to

        gig_id:     null,
        // trip notifications have no associated gig.
        // The column allows NULL (no NOT NULL / DEFAULT in schema)
        // and PostgreSQL does not enforce FK constraints on NULL.

        notif_type: notifType,
        // identifies the notification kind AND encodes the trip ID
        // for clearForTrip's LIKE query

        send_at:    sendAtDate.toISOString(),
        // UTC timestamp the Edge Function cron checks every minute

        sent:       false,
        // Edge Function sets this true after successful delivery
      })
    }


    // --------------------------------------------------------
    // MULTI-LEG TRIP: one departure block per leg
    // --------------------------------------------------------
    if (trip.trip_type === 'multi_leg' && trip.legs?.length) {

      trip.legs.forEach((leg, i) => {
        // i = leg index, used as the suffix so clearForTrip can
        // delete individual-leg rows alongside root-level rows

        if (!leg.depart_at) return
        // leg has no departure time -- skip it (incomplete form data)

        // Warn before the performer needs to leave for this leg's terminal
        addRow(
          `travel_leave_warning:${trip.id}:${i}`,
          toSendAt(leg.depart_at, -advanceMs)
          // negative offset = fires advanceMin minutes BEFORE departure
        )

        // Fire exactly when it is time to leave for the terminal
        addRow(
          `travel_leave_now:${trip.id}:${i}`,
          toSendAt(leg.depart_at)
          // no offset = fires at the departure moment
        )

        // Optional per-leg pickup (e.g. a taxi arriving to take
        // the performer to the airport for this leg)
        if (leg.pickup_time) {
          addRow(
            `travel_pickup_warning:${trip.id}:${i}`,
            toSendAt(leg.pickup_time, -advanceMs)
          )
          addRow(
            `travel_pickup_now:${trip.id}:${i}`,
            toSendAt(leg.pickup_time)
          )
        }
      })

    } else {
      // --------------------------------------------------------
      // ROUND TRIP: outbound + return + optional pickup
      // --------------------------------------------------------

      // Outbound: performer leaves home to go to the origin terminal
      if (trip.depart_home_at) {
        addRow(
          `travel_leave_warning:${trip.id}`,
          toSendAt(trip.depart_home_at, -advanceMs)
        )
        addRow(
          `travel_leave_now:${trip.id}`,
          toSendAt(trip.depart_home_at)
        )
      }

      // Return: performer leaves the destination to go back to
      // the origin terminal and then home
      if (trip.depart_dest_at) {
        addRow(
          `travel_return_warning:${trip.id}`,
          toSendAt(trip.depart_dest_at, -advanceMs)
        )
        addRow(
          `travel_return_now:${trip.id}`,
          toSendAt(trip.depart_dest_at)
        )
      }

      // Optional pickup for the outbound journey
      // (e.g. a taxi arriving to take the performer to the airport)
      // Set trip.pickup_time in AddTripModal to enable these rows.
      if (trip.pickup_time) {
        addRow(
          `travel_pickup_warning:${trip.id}`,
          toSendAt(trip.pickup_time, -advanceMs)
        )
        addRow(
          `travel_pickup_now:${trip.id}`,
          toSendAt(trip.pickup_time)
        )
      }
    }


    // --------------------------------------------------------
    // Insert all rows in one batch request
    // --------------------------------------------------------
    if (rows.length === 0) {
      console.log(
        '[useScheduleTripNotifications] No rows to schedule for trip', trip.id,
        '(all times stale or missing).'
      )
      return
    }

    const { error } = await supabase
      .from('scheduled_notifications')
      .insert(rows)
      // insert the full batch in a single round-trip

    if (error) {
      console.error('[useScheduleTripNotifications] Batch insert error:', error.message)
    } else {
      console.log(
        `[useScheduleTripNotifications] Scheduled ${rows.length} notification(s) for trip ${trip.id}:`,
        rows.map(r => `${r.notif_type} @ ${r.send_at}`)
        // log every type + time so you can verify the schedule is correct
      )
    }
  }, [clearForTrip])


  return {
    scheduleForTrip,
    // (trip: object, userId: string) => Promise<void>
    // call after every addTrip / updateTrip

    clearForTrip,
    // (tripId: string, userId: string) => Promise<void>
    // call before deleteTrip to remove all notification rows for the trip
  }
}
